/**
 * Pure helper functions for income statement-style grid calculations.
 * These are extracted from the component so they can be unit-tested independently.
 */

/**
 * Maps an account type string to its income statement display section and sort order.
 * Handles both full names (e.g. 'Revenue') and abbreviations (e.g. 'Rev').
 *
 * @param {string} acctType - Account type from the database
 * @returns {{ section: string, sortOrder: number, subOrder: number }}
 */
export const getAcctTypeSection = (acctType) => {
  if (!acctType || acctType === '') {
    return { section: 'Other', sortOrder: 99, subOrder: 1 };
  }

  const normalizedType = String(acctType).trim();

  // Section 1: Net Patient Revenue (Revenue on top, Deductions below)
  if (normalizedType === 'Revenue' || normalizedType === 'Rev') {
    return { section: 'Net Patient Revenue', sortOrder: 1, subOrder: 1 };
  }
  if (normalizedType === 'Deduction' || normalizedType === 'Ded') {
    return { section: 'Net Patient Revenue', sortOrder: 1, subOrder: 2 };
  }

  // Section 2: Other Revenue
  if (normalizedType === 'Other Revenue' || normalizedType === 'Oth Rev') {
    return { section: 'Other Revenue', sortOrder: 2, subOrder: 1 };
  }

  // Section 3: Operating Expenses
  if (normalizedType === 'Expense' || normalizedType === 'Exp') {
    return { section: 'Operating Expenses', sortOrder: 3, subOrder: 1 };
  }

  // Section 4: Statistics
  if (normalizedType === 'Stat') {
    return { section: 'Statistics', sortOrder: 4, subOrder: 1 };
  }

  // Special cases
  if (normalizedType === 'Master Stat') {
    return { section: 'Statistics', sortOrder: 4, subOrder: 0 };
  }
  if (normalizedType === 'Calculated Account') {
    return { section: 'Calculated Accounts', sortOrder: 5, subOrder: 1 };
  }

  // Default to Other for unknown types
  if (process.env.NODE_ENV === 'development') {
    console.warn(`Unknown account type encountered: "${normalizedType}"`);
  }
  return { section: 'Other', sortOrder: 99, subOrder: 1 };
};

/**
 * Calculates Net Patient Revenue (Revenue - Deductions) for a given month
 * from a flat array of account rows.
 *
 * @param {Array<Object>} rows - Flat array of account data rows
 * @param {string} month - Column key (month field) to aggregate
 * @returns {number}
 */
export const calculateNetPatientRevenue = (rows, month) => {
  let revenueSum = 0;
  let deductionSum = 0;

  rows.forEach((row) => {
    const section = row.acctTypeSection;
    if (section === 'Net Patient Revenue') {
      const acctType = row.acctType;
      const value = row[month] || 0;
      if (acctType === 'Revenue' || acctType === 'Rev') {
        revenueSum += value;
      } else if (acctType === 'Deduction' || acctType === 'Ded') {
        deductionSum += value;
      }
    }
  });

  return revenueSum - deductionSum;
};

/**
 * Calculates Net Revenue (Net Patient Revenue + Other Revenue) for a given month.
 *
 * @param {Array<Object>} rows - Flat array of account data rows
 * @param {string} month - Column key (month field) to aggregate
 * @returns {number}
 */
export const calculateNetRevenue = (rows, month) => {
  const npr = calculateNetPatientRevenue(rows, month);
  const otherRevenue = rows
    .filter((row) => row.acctTypeSection === 'Other Revenue')
    .reduce((sum, row) => sum + (row[month] || 0), 0);
  return npr + otherRevenue;
};

/**
 * Calculates Bottom Line (Net Revenue - Operating Expenses) for a given month.
 *
 * @param {Array<Object>} rows - Flat array of account data rows
 * @param {string} month - Column key (month field) to aggregate
 * @returns {number}
 */
export const calculateBottomLine = (rows, month) => {
  const netRevenue = calculateNetRevenue(rows, month);
  const operatingExpenses = rows
    .filter((row) => row.acctTypeSection === 'Operating Expenses')
    .reduce((sum, row) => sum + (row[month] || 0), 0);
  return netRevenue - operatingExpenses;
};

/**
 * Inserts synthetic Net Revenue department rows into a flat rowData array.
 * Departments are derived from the Net Patient Revenue and Other Revenue rows.
 * The AG Grid acctTypeSection group row acts as the Net Revenue summary
 * (values computed via netRevenueAggFunc), so no extra synthetic parent row is added.
 *
 * @param {Array<Object>} rows - Existing flat row array
 * @param {string[]} departmentsFromBackend - Optional: Pre-extracted department list for lazy loading
 * @returns {Array<Object>} Updated row array with Net Revenue department rows inserted
 */
export const addNetRevenueSummaryRow = (rows, departmentsFromBackend = null) => {
  if (!rows || rows.length === 0) {
    return rows;
  }

  let uniqueDepartments;
  
  if (departmentsFromBackend && departmentsFromBackend.length > 0) {
    // Use departments from backend or extracted from GL keys (for lazy loading).
    // Normalize to remove falsy values and deduplicate, matching the row-derived path.
    uniqueDepartments = [...new Set(departmentsFromBackend.filter(Boolean))];
  } else {
    // Extract from rows (for backward compatibility when all detail rows are loaded).
    // Exclude lazy-load placeholder rows that use "Total" as a sentinel department.
    const relevantRows = rows.filter(
      (row) =>
        (row.acctTypeSection === 'Net Patient Revenue' ||
         row.acctTypeSection === 'Other Revenue') &&
        row.department !== 'Total' && // exclude lazy-load placeholders
        !row.isNetRevenueDepartment &&
        !row.isBottomLineDepartment
    );
    uniqueDepartments = [
      ...new Set(relevantRows.map((row) => row.department)),
    ].filter(Boolean);
  }

  const netRevenueDepartmentRows = uniqueDepartments.map((dept) => ({
    id: `NET-REVENUE-${dept}`,
    glKey: `NET-REVENUE-${dept}`,
    isNetRevenueDepartment: true,
    acctTypeSection: 'Net Revenue',
    acctType: 'Net Revenue',
    acctTypeSortOrder: 2.5,
    // Use a guaranteed-unique parentAccount prefix so these rows never collide with real
    // account data at the level-1 group. Each department gets its own level-1 group node
    // keyed by __NR__<dept>, and a level-3 accountName group also keyed by __NR__<dept>.
    // Both are hidden via CSS/rowHeight so only the level-2 department group is visible.
    parentAccount: `__NR__${dept}`,
    department: dept,
    accountName: `__NR__${dept}`,
  }));

  const insertIndex = rows.findIndex((row) => row.acctTypeSortOrder >= 3);
  const rowsToInsert = netRevenueDepartmentRows;

  if (insertIndex === -1) {
    return [...rows, ...rowsToInsert];
  }

  return [
    ...rows.slice(0, insertIndex),
    ...rowsToInsert,
    ...rows.slice(insertIndex),
  ];
};

/**
 * Inserts synthetic Bottom Line department rows into a flat rowData array.
 * Departments are derived from all revenue and expense rows.
 * The AG Grid acctTypeSection group row acts as the Bottom Line summary
 * (values computed via aggregation), so no extra synthetic parent row is added.
 *
 * @param {Array<Object>} rows - Existing flat row array
 * @returns {Array<Object>} Updated row array with Bottom Line department rows inserted
 */
export const addBottomLineSummaryRow = (rows, departmentsFromBackend = null) => {
  if (!rows || rows.length === 0) {
    return rows;
  }

  let uniqueDepartments;

  if (departmentsFromBackend && departmentsFromBackend.length > 0) {
    uniqueDepartments = [...new Set(departmentsFromBackend.filter(Boolean))];
  } else {
    // Fallback: derive from loaded rows, but only use rows that have real department
    // values (not lazy-load placeholders which use "Total" as a sentinel).
    const relevantRows = rows.filter(
      (row) =>
        (row.acctTypeSection === 'Net Patient Revenue' ||
         row.acctTypeSection === 'Other Revenue' ||
         row.acctTypeSection === 'Operating Expenses') &&
        row.department !== 'Total' && // exclude lazy-load placeholders
        !row.isNetRevenueDepartment &&
        !row.isBottomLineDepartment
    );

    uniqueDepartments = [
      ...new Set(relevantRows.map((row) => row.department)),
    ].filter(Boolean);
  }

  const bottomLineDepartmentRows = uniqueDepartments.map((dept) => ({
    id: `BOTTOM-LINE-${dept}`,
    glKey: `BOTTOM-LINE-${dept}`,
    isBottomLineDepartment: true,
    acctTypeSection: 'Bottom Line',
    acctType: 'Bottom Line',
    acctTypeSortOrder: 3.5,
    // Use a guaranteed-unique parentAccount prefix so these rows never collide with real
    // account data at the level-1 group. Each department gets its own level-1 group node
    // keyed by __BL__<dept>, and a level-3 accountName group also keyed by __BL__<dept>.
    // Both are hidden via CSS/rowHeight so only the level-2 department group is visible.
    parentAccount: `__BL__${dept}`,
    department: dept,
    accountName: `__BL__${dept}`,
  }));

  const insertIndex = rows.findIndex((row) => row.acctTypeSortOrder >= 4);
  const rowsToInsert = bottomLineDepartmentRows;

  if (insertIndex === -1) {
    return [...rows, ...rowsToInsert];
  }

  return [
    ...rows.slice(0, insertIndex),
    ...rowsToInsert,
    ...rows.slice(insertIndex),
  ];
};
